import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Plotly from "plotly.js/dist/plotly";

import {
  getAnalysisJob,
  getAnalysisStatus,
  getAnalysis,
  getType,
  getLifetimeWordReport,
  getLastFileDetails,
} from "../../../network/cipAPI";
import {
  storeAnalysisData,
  storeTaskId,
} from "../../../Store/TemplateStore/template.action";

import ReplacementResultChart from "../charts";
import PageLoading from "../../../components/PageLoading";
import { OverlayLoader } from "../../../components/PageLoading";
import AgTable from "../../../components/AgTable";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";
import exportAllReplacementTables from "../../../XLSDownload/ExportAllTables/raTableExport";
import {
  fetchSuggestedSettings,
  formatSuggestedSettings,
} from "../../../utils/Utils";
import { WPRangedInput } from "../../../packages/WPInput/WPRangedInput";
import { AccordionDownIcon } from "../../../packages/SVGs";
import WPLoader from "../../../packages/WPLoader/WPLoader.jsx";
import { WPDropDownMenu } from "../../../packages/WPDropDownMenu/WPDropDownMenu.jsx";
import IconButton from "../../../packages/IconButton";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";
import { QuestionIcon, SettingsIcon } from "../../../assets/icons";
import CustomModal from "../../../packages/CustomModal";
import StatusApiErrorDialog from "../../../components/StatusAPIErrorDialog/index.jsx";
import {
  CHART_EXPORT_CONFIG,
  FORECAST_WINDOW,
  MODELING_UI_TEXT_KEYS,
  PLOTLY_CLASS_NAME,
  STATUS_API_ERROR_KEYS,
} from "../../../constants/global.constants";
import { createDownloadMenuItems } from "../../../common/configs/downloadMenuConfig";
import ExportAllData from "./exportAllData";
import { RELACEMENT_ANALYSIS_COL_DEF } from "./replacementAnalysisTable.config";
import { generateFileName } from "../../../utils/Utils.js";
import WPButton from "../../../packages/WPButton/index.jsx";
import DupontCheckbox from "../../../components/DupontCheckbox/index.js";

import "./index.scss";
import AdvancedSettings from "../../analyseData/AdvancedSettings/AdvancedSettings.jsx";

const ReplacementResult = ({
  setDisableNextButton,
  detailsResult,
  setSettingsButton,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const intervalIdRef = useRef(null);
  const chartRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState("1%");
  const [taskId, setTaskId] = useState(useSelector((state) => state.taskId));
  const [rowData, setRowData] = useState(null);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [translatedRowData, setTranslatedRowData] = useState(null);
  const [isWordDownloading, setIsWordDownloading] = useState(false);
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [errorMsgFailedPrediction, setErrorMsgFailedPrediction] =
    useState(null);
  const [recommendedCipsFlag, setRecommendedCipsFlag] = useState(0);

  const selectedTrain = useSelector((state) => state.selTrain);
  const installationDate = useSelector((state) => state.installation_date);

  const handleUseRecommendedCipsChange = (isChecked) => {
    setRecommendedCipsFlag(isChecked ? 1 : 0);
  };

  const downloadFileName = useMemo(
    () =>
      generateFileName(
        t("replacementAnalysisStrings.replacementAdvisorReport"),
      ),
    [t],
  );

  const allData = ExportAllData();
  const [exportAllData, setExportAllData] = useState({
    ...allData,
    replacementAnalysisData: rowData,
    replacementPredictionData: plotData,
  });
  const [forecastDays, setForecastDays] = useState(
    useSelector((state) => state.forecast_window),
  );
  const [lastAnalyzedForecast, setLastAnalyzedForecast] = useState(null);
  const [lastAnalyzedRecommendedCipsFlag, setLastAnalyzedRecommendedCipsFlag] =
    useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isNoStablePointsDialogOpen, setIsNoStablePointsDialogOpen] =
    useState(false);
  const [isNoPredictedCipsDialogOpen, setIsNoPredictedCipsDialogOpen] =
    useState(false);
  const [sameCIPUITextkey, setSameCIPUITextkey] = useState("");
  const colDef = RELACEMENT_ANALYSIS_COL_DEF(t, translatedRowData);
  const shouldShowSameClPInfoBanner =
    sameCIPUITextkey === MODELING_UI_TEXT_KEYS.PREDICTED_RECOMMENDED_CIPS_SAME;

  const isAnalysisInputUnchanged =
    forecastDays === lastAnalyzedForecast &&
    recommendedCipsFlag === lastAnalyzedRecommendedCipsFlag;

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const handleSettingsOpen = () => {
    setIsSettingsDialogOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const onHelpBtnClick = () => {
    setHelpDialogOpen(true);
  };

  const handleAdvanceSettingsSave = () => {
    // Trigger the analysis API call again when advance settings are saved
    onReplacementResultClicked();
  };

  useEffect(() => {
    setSettingsButton?.(
      <WPTertiaryButton
        onClick={handleSettingsOpen}
        disabled={isLoading}
        label={t("analyseData.settings")}
        icon={<SettingsIcon />}
        iconPlacement="before"
        size="medium"
        variant="outlined"
      />,
    );
    return () => setSettingsButton?.(null);
  }, [isLoading]);

  useEffect(() => {
    setExportAllData({
      ...allData,
      replacementAnalysisData: rowData,
      replacementPredictionData: plotData,
    });
  }, [rowData, plotData, allData]);

  const handleForecastChange = (e) => {
    const value = parseInt(e?.target?.value ?? e) || null;
    setErrorMsg("");

    if (value && value >= FORECAST_WINDOW.MIN && value <= FORECAST_WINDOW.MAX) {
      setForecastDays(value);
      setExportAllData((prevState) => ({
        ...prevState,
        others: [
          {
            ...prevState.others[0],
            forecast_window: value,
          },
        ],
      }));
    } else {
      setForecastDays(value);
      setErrorMsg(t("replacementAnalysisStrings.forecastWindowError"));
    }
  };

  const checkStatus = async (task_id) => {
    try {
      let id = task_id || taskId;
      let analysisStatus;
      if (id === "" || id === null || id === undefined) {
        analysisStatus = { data: { status: "pending" } };
      } else {
        analysisStatus = await getAnalysisStatus(id);
      }

      let { status, processed, error } = analysisStatus?.data || {};
      const isNoStablePointsError =
        error === STATUS_API_ERROR_KEYS.NO_STABLE_POINTS;
      const isNoPredictedCipsError =
        error === STATUS_API_ERROR_KEYS.NO_PREDICTED_CIPS;
      status = status?.toLowerCase();

      try {
        if (status === "completed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          const analysisResult = await getAnalysis(
            selectedTrain,
            forecastDays,
            recommendedCipsFlag,
          );

          setShowOverlayLoader(false);
          setIsNoStablePointsDialogOpen(false);
          setIsNoPredictedCipsDialogOpen(false);
          setIsLoading(false);
          setDisableNextButton(false);
          if (analysisResult?.data["modeling"]) {
            try {
              const parsedData = JSON.parse(analysisResult?.data["modeling"]);
              dispatch(storeAnalysisData(parsedData));
              setRowData(parsedData.replacement_results);
              setPlotData(parsedData.plot_results);
              setSameCIPUITextkey(parsedData?.ui_text || "");

              dispatch(storeTaskId(""));
              setTaskId("");
            } catch (error) {
              setSameCIPUITextkey("");
              createNotification(
                ERROR_NOTIFICATION,
                t("notificationStrings.somethingWrong"),
                "",
              );
            } finally {
              setDisableNextButton(false);
              setIsLoading(false);
              setLastAnalyzedForecast(forecastDays);
              setLastAnalyzedRecommendedCipsFlag(recommendedCipsFlag);
            }
          }
        } else if (status === "running") {
          setprocessedPercentage(processed);
          setIsLoading(true);
          setDisableNextButton(true);
          setShowOverlayLoader(false);
        } else if (status === "failed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setDisableNextButton(false);
          setShowOverlayLoader(false);

          if (isNoStablePointsError) {
            setIsNoStablePointsDialogOpen(true);
            setIsNoPredictedCipsDialogOpen(false);
            setErrorMsgFailedPrediction("");
          } else if (isNoPredictedCipsError) {
            setIsNoPredictedCipsDialogOpen(true);
            setIsNoStablePointsDialogOpen(false);
            setErrorMsgFailedPrediction("");
          } else {
            createNotification(
              ERROR_NOTIFICATION,
              error || t("predictionStrings.failedPrediction"),
              "",
            );
            setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
          }

          dispatch(storeTaskId(""));
          setTaskId("");
          setRowData(null);
          setPlotData(null);
          setSameCIPUITextkey("");
          dispatch(storeAnalysisData(null));
        } else if (status === "pending") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setShowOverlayLoader(false);
          setDisableNextButton(false);
          setSameCIPUITextkey("");
        }
      } catch (error) {
        setShowOverlayLoader(false);
        setIsLoading(false);
        setDisableNextButton(false);
        console.error("Error fetching data:", error);
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || error.response.status === 502) {
        console.log("Error is " + error);
      } else {
        console.error("Error fetching data:", error);
        setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
        setIsLoading(false);
        setDisableNextButton(false);
        setShowOverlayLoader(false);
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        dispatch(storeTaskId(""));
        setTaskId("");
      }
    }
  };

  const onReplacementResultClicked = async () => {
    setIsLoading(true);
    setIsNoStablePointsDialogOpen(false);
    setIsNoPredictedCipsDialogOpen(false);
    setDisableNextButton(true);
    setprocessedPercentage("1%");
    setErrorMsgFailedPrediction(null);
    setSameCIPUITextkey("");

    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    try {
      const convertedType = await getType();
      setTranslatedRowData(convertedType.data);
      let response = await getAnalysisJob(
        forecastDays,
        selectedTrain,
        recommendedCipsFlag,
      );
      const task_id = response?.data?.task_id;
      dispatch(storeTaskId(task_id));
      setTaskId(task_id);

      intervalIdRef.current = setInterval(function () {
        checkStatus(task_id);
      }, 15000);
    } catch (error) {
      setIsLoading(false);
      setDisableNextButton(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  useEffect(() => {
    const fetchPreviousData = async () => {
      try {
        setIsLoading(true);
        setDisableNextButton(true);
        const convertedType = await getType();
        setTranslatedRowData(convertedType.data);

        // Fetch last file details to get previous_ra_prediction
        const fileDetails = await getLastFileDetails(selectedTrain);
        const { previous_ra_prediction } = fileDetails.data;
        if (previous_ra_prediction) {
          const { modeling, forecast_window, recommended_cips_flag } =
            previous_ra_prediction;

          if (modeling) {
            const parsedData = JSON.parse(modeling);
            dispatch(storeAnalysisData(parsedData));
            setRowData(parsedData.replacement_results);
            setPlotData(parsedData.plot_results);
            setSameCIPUITextkey(parsedData?.ui_text || "");

            if (forecast_window) {
              setForecastDays(Math.ceil(forecast_window));
              setLastAnalyzedForecast(Math.ceil(forecast_window));
            }
            if (recommended_cips_flag !== undefined) {
              setRecommendedCipsFlag(recommended_cips_flag);
              setLastAnalyzedRecommendedCipsFlag(recommended_cips_flag);
            }
          }
        } else {
          dispatch(storeAnalysisData(null));
          setRowData(null);
          setPlotData(null);
          setSameCIPUITextkey("");
        }

        // Clear any existing taskId to allow new analysis
        dispatch(storeTaskId(""));
        setTaskId("");
      } catch (error) {
        console.error("Error fetching previous analysis data:", error);
      } finally {
        setIsLoading(false);
        setDisableNextButton(false);
      }
    };

    // Only fetch data when detailsResult becomes true (after PUT API completes)
    if (detailsResult) {
      fetchPreviousData();
    } else {
      // If detailsResult is false/undefined, ensure loading state is turned off
      setIsLoading(false);
      setDisableNextButton(false);
      // Clear any existing taskId
      dispatch(storeTaskId(""));
      setTaskId("");
    }

    // Cleanup function when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [detailsResult]);

  const handleExcelExport = async () => {
    try {
      setIsExcelDownloading(true);
      const suggestedSettings = await fetchSuggestedSettings(
        selectedTrain,
        dispatch,
      );

      const suggestedSettingsForExport = suggestedSettings
        ? formatSuggestedSettings(suggestedSettings, t)
        : null;

      const exportDataWithSuggested = {
        ...exportAllData,
        suggestedOthers: suggestedSettingsForExport,
        // Keep export aligned with last analyzed toggle state.
        recommendedCipsFlag: lastAnalyzedRecommendedCipsFlag,
      };

      await exportAllReplacementTables(
        downloadFileName,
        exportDataWithSuggested,
        translatedRowData,
        t,
        selectedTrain,
      );
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsExcelDownloading(false);
    }
  };

  // Converts Plotly chart to base64 PNG for Word export
  //Temporarily resize chart → capture image → restore original layout
  const convertChartToBase64 = async (
    plotlyDiv,
    exportHeight,
    exportMarginTop,
    exportMarginBottom,
  ) => {
    const rect = plotlyDiv?.getBoundingClientRect();
    const currentWidth = Math.round(rect?.width || plotlyDiv?.clientWidth || 0);
    const currentHeight = Math.round(
      rect?.height || plotlyDiv?.clientHeight || 0,
    );
    let originalLayout = null;

    try {
      if (Plotly?.toImage && currentWidth > 0 && currentHeight > 0) {
        // Store original layout for restoration
        const { margin, xaxis, yaxis } = plotlyDiv?.layout || {};
        originalLayout = {
          width: currentWidth,
          height: currentHeight,
          "margin.b": margin?.b,
          "margin.t": margin?.t,
          "xaxis.automargin": xaxis?.automargin,
          "yaxis.automargin": yaxis?.automargin,
        };

        // Temporarily resize chart with explicit margins
        await Plotly.relayout(plotlyDiv, {
          width: currentWidth,
          height: exportHeight,
          "margin.b": exportMarginBottom,
          "margin.t": exportMarginTop,
          "xaxis.automargin": false,
          "yaxis.automargin": false,
        });
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const exportedImage = await Plotly.toImage(plotlyDiv, {
          format: "png",
          width: currentWidth,
          height: exportHeight,
          scale: 1,
        });
        if (exportedImage) {
          return exportedImage.replace(/^data:image\/png;base64,/, "");
        }
      }
      return null;
    } catch {
      return null;
    } finally {
      // Restore original layout
      if (originalLayout) {
        await Plotly.relayout(plotlyDiv, originalLayout);
      }
    }
  };

  const handleWordDownload = async () => {
    try {
      setIsWordDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Chart export dimensions from global constants
      const { PLOT_AREA_HEIGHT, MARGIN_TOP, MARGIN_BOTTOM } =
        CHART_EXPORT_CONFIG;
      const exportHeight = PLOT_AREA_HEIGHT + MARGIN_TOP + MARGIN_BOTTOM;
      // Get all Plotly chart divs from the chart container
      const plotlyDivs =
        chartRef.current?.querySelectorAll(PLOTLY_CLASS_NAME.CLASS_NAME) || [];

      // Convert all charts to base64 in parallel
      const chartConversion = Array.from(plotlyDivs).map((plotlyDiv) =>
        convertChartToBase64(
          plotlyDiv,
          exportHeight,
          MARGIN_TOP,
          MARGIN_BOTTOM,
        ),
      );

      const charts = (await Promise.all(chartConversion)).filter(Boolean);
      const payload = {
        charts,
        replacement_results: rowData || [],
        forecast_window: forecastDays,
        installation_date: installationDate,
        recommended_cips_flag: lastAnalyzedRecommendedCipsFlag,
      };

      // Call API
      const response = await getLifetimeWordReport(selectedTrain, payload);

      if (response && response.status === 200) {
        const blob = response.data;
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${downloadFileName}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);
        }, 100);
      }
    } catch (error) {
      console.error("Word download error:", error);
      createNotification(
        ERROR_NOTIFICATION,
        t("predictionStrings.wordReportDownloadFailed"),
        "",
      );
    } finally {
      setIsWordDownloading(false);
    }
  };

  const downloadMenuItems = createDownloadMenuItems(
    t,
    rowData !== null,
    isLoading,
    handleExcelExport,
    handleWordDownload,
  );

  return (
    <div className="ag-theme-alpine container-padding-ra">
      {showOverlayLoader && <OverlayLoader customHeight={"350px"} />}
      <div className="descriptionText uploadContainer">
        {t("replacementAnalysisStrings.forecastDescription")}
      </div>
      <div className="flex-center-center">
        <div className="fitContent">
          <WPRangedInput
            value={forecastDays}
            label={t("replacementAnalysisStrings.forecastWindow")}
            onChange={handleForecastChange}
            ranges={{
              hard: { min: FORECAST_WINDOW.MIN, max: FORECAST_WINDOW.MAX },
            }}
            integer
            disabled={isLoading}
          />
        </div>
        <DupontCheckbox
          labelText={t("replacementAnalysisStrings.useRecommendedCips")}
          checked={recommendedCipsFlag === 1}
          onChange={handleUseRecommendedCipsChange}
          customClass="recommended-cips-checkbox"
          disabled={isLoading}
        />
        <div className="info-section">
          <WPButton
            onClick={onReplacementResultClicked}
            customClass="margin-left-10"
            disabled={
              !forecastDays || isLoading || errorMsg || isAnalysisInputUnchanged
            }
          >
            {t("replacementAnalysisStrings.runReplacementAnalysis")}
          </WPButton>
          <IconButton onClick={onHelpBtnClick}>
            <QuestionIcon />
          </IconButton>
        </div>
        <div className={`text-right margin-left`}>
          <WPDropDownMenu
            toggle={
              <div className="predict-download-toggle">
                {t("predictionStrings.downloadReport")}
                <AccordionDownIcon />
              </div>
            }
            disabled={rowData === null || isLoading}
            customClass="predict-dropdown-tertiary"
            menuItems={downloadMenuItems}
          />
        </div>
      </div>
      {isWordDownloading && (
        <WPLoader loadingMsg={t("predictionStrings.generatingWordReport")} />
      )}
      {isExcelDownloading && (
        <WPLoader loadingMsg={t("predictionStrings.generatingExcelReport")} />
      )}
      {isLoading ? (
        <PageLoading
          customHeight={150}
          iconHeight={"height--100"}
          message={t("ValueStrings.loaderMessage")}
          percentage={processedPercentage}
        ></PageLoading>
      ) : (
        <div>
          {errorMsgFailedPrediction && (
            <div className="errorMsg">{errorMsgFailedPrediction}</div>
          )}

          {rowData && shouldShowSameClPInfoBanner && (
            <WPMessageBanner
              type={BANNER_TYPES.info}
              message={t(
                "replacementAnalysisStrings.predictedRecommendedCipsSameInfo",
              )}
            />
          )}

          {rowData && (
            <AgTable
              rowData={rowData}
              columnDefs={colDef}
              domLayout="autoHeight"
              name={`${t("excelSheetNames.replacementAnalysis")}.csv`}
            />
          )}
          {plotData && (
            <div className="margin-top-20" ref={chartRef}>
              <div className="descriptionText marginBtm10">
                {t("replacementAnalysisStrings.chartDescription")}
              </div>
              <ReplacementResultChart plotData={plotData} />
            </div>
          )}

          {rowData && (
            <div className="ro-disclaimer">
              {t("disclaimer", { currentYear })}
            </div>
          )}
        </div>
      )}

      {isSettingsDialogOpen && (
        <AdvancedSettings
          handleClose={handleSettingsClose}
          selectedTrain={selectedTrain}
          cipPredictionAndRecommendation
          lifetime
          onSave={handleAdvanceSettingsSave}
          excludeFields={["max_evals", "optimization_target"]}
        />
      )}
      {helpDialogOpen && (
        <CustomModal
          show={helpDialogOpen}
          confirmBtn={t("ValueStrings.okay")}
          width="575px"
          size="small"
          header={t("helpInformation.predictionHelp")}
          onModalClose={() => setHelpDialogOpen(false)}
        >
          <div className="help-dialog-content">
            <div className="help-section">
              <ul className="help-list">
                <li className="help-list-item">
                  {t("helpInformation.predictionHelpModifyThreshold")}
                </li>
                <li className="help-list-item">
                  {t("helpInformation.predictionHelpAlterSettings")}
                </li>
              </ul>
            </div>
          </div>
        </CustomModal>
      )}

      {(isNoStablePointsDialogOpen || isNoPredictedCipsDialogOpen) && (
        <StatusApiErrorDialog
          show={isNoStablePointsDialogOpen || isNoPredictedCipsDialogOpen}
          dialogType={
            isNoPredictedCipsDialogOpen
              ? STATUS_API_ERROR_KEYS.NO_PREDICTED_CIPS
              : STATUS_API_ERROR_KEYS.NO_STABLE_POINTS
          }
          isLifetime
          onClose={() => {
            setIsNoStablePointsDialogOpen(false);
            setIsNoPredictedCipsDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ReplacementResult;
